import { ReportingItem } from "vmd-types";

import { TestAppState } from "../../app";
import {
  CRITICAL_CRITICALITY_LEVELS,
  DONUT_CHART_COLORS,
} from "../../constants";

/**
 * Shared helpers every "systems per X" bar/donut chart card in the
 * dashboard-numbers row of the overview page used to close over inline,
 * inside the 3,800-line `buildOverview()` monolith (#2222).
 *
 * Each `addXCards` function under `./cards` calls these itself rather than
 * receiving the values through a shared context object — cheap to recompute,
 * and it keeps every card function self-contained.
 */

export const criticalityLevelSet = new Set<string>(CRITICAL_CRITICALITY_LEVELS);

export const withTotalInTitle = (title: string, total: number) =>
  `${title} (${total})`;

export type DashboardChartEntry = {
  label: string;
  count: number;
  color: string;
};

export const getAllReportingItems = (
  dashboard: TestAppState["dashboard"] | undefined
): ReportingItem[] => {
  return (dashboard?.projects ?? [])
    .flatMap((project) => project.reportingItems ?? [])
    .filter((item) => !item?.isChannelHeader);
};

export const getDistributionChannelData = (
  items: ReportingItem[],
  distributionChannelDefinition?: { id: string; label: string }[]
): { entries: DashboardChartEntry[] } => {
  const channelCounts = new Map<string, { label: string; count: number }>();

  for (const item of items) {
    const channelIds = item.distributionChannel ?? [];
    if (!channelIds.length) {
      continue;
    }

    for (const channelId of channelIds) {
      const existing = channelCounts.get(channelId) || {
        label:
          distributionChannelDefinition?.find((c) => c.id === channelId)
            ?.label || channelId,
        count: 0,
      };

      channelCounts.set(channelId, {
        ...existing,
        count: existing.count + 1,
      });
    }
  }

  const entries = Array.from(channelCounts.values())
    .sort((a, b) => b.count - a.count)
    .map((entry, index) => ({
      label: entry.label,
      count: entry.count,
      color: DONUT_CHART_COLORS[index % DONUT_CHART_COLORS.length],
    }));

  return { entries };
};

export const getArrayFieldData = (
  items: ReportingItem[],
  picker: (item: ReportingItem) => string[] | undefined
): { entries: DashboardChartEntry[] } => {
  const counts = new Map<string, number>();

  for (const item of items) {
    for (const value of picker(item) ?? []) {
      if (!value) {
        continue;
      }
      counts.set(value, (counts.get(value) ?? 0) + 1);
    }
  }

  const entries = Array.from(counts.entries())
    .sort((a, b) => b[1] - a[1])
    .map(([label, count], index) => ({
      label,
      count,
      color: DONUT_CHART_COLORS[index % DONUT_CHART_COLORS.length],
    }));

  return { entries };
};

export const getLifeCycleData = (
  items: ReportingItem[]
): { entries: DashboardChartEntry[] } => {
  const counts = new Map<string, number>();

  for (const item of items) {
    const lifeCycle = item.basicItem?.lifeCycle;
    if (!lifeCycle) {
      continue;
    }
    counts.set(lifeCycle, (counts.get(lifeCycle) ?? 0) + 1);
  }

  const entries = Array.from(counts.entries())
    .sort((a, b) => b[1] - a[1])
    .map(([label, count], index) => ({
      label,
      count,
      color: DONUT_CHART_COLORS[index % DONUT_CHART_COLORS.length],
    }));

  return { entries };
};
